import { Directive, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  AssetPreviewInput,
  MalwareScanningStatus,
  ThumbnailPosition,
  FileTranscodeInfo,
  FileInfo,
} from '../interfaces';
import { MalwareScanMonitorService } from '../services/signed-url/malware-scan-monitor.service';
import { TranslatePipe } from '../pipes/translate.pipe';
import { FileTransferService, listenerUploadProgressChanged, MTMCustomFile } from '../services/signed-url';
import { FileUtilsService } from '../services/file-utils.service';
import { FileTypeService } from '../services/file-type.service';
import { FILE_ICONS } from '../constants/file-icons.constants';
import { AssetPreviewMediaType, FileExtension } from '../models';
import { AssetIconPaths } from '../constants/asset-icons.constants';

@Directive({
  selector: '[assetPreviewDirective]',
})
export class AssetPreviewDirective implements OnInit, OnChanges, OnDestroy {
  @Input() assetPreviewDirective: AssetPreviewInput;
  @Input() assetPreviewThumbnailPosition: ThumbnailPosition;
  @Input() assetPreviewId: string;

  timeInterval: ReturnType<typeof setTimeout> | null = null;
  retryTime: number = 5000; // In ms
  ngUnsubscribe: Subject<void> = new Subject<void>();
  childEl: HTMLElement | null = null;
  scanUpdateSubscription: Subscription;
  uploadProgressSubscription: Subscription;

  constructor(
    public elementRef: ElementRef,
    private readonly scanMonitorService: MalwareScanMonitorService,
    private readonly translatePipe: TranslatePipe,
    private readonly transferService: FileTransferService,
    private readonly fileTypeService: FileTypeService,
    private readonly fileUtilsService: FileUtilsService
  ) {}

  ngOnInit(): void {
    this.init();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const relevantInputs: string[] = ['assetPreviewDirective', 'assetPreviewThumbnailPosition', 'assetPreviewId'];
    const hasRelevantChange: boolean = relevantInputs.some(
      (input: string) => changes[input] && !changes[input].firstChange
    );
    if (hasRelevantChange) {
      this.init();
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next(undefined);

    this.ngUnsubscribe.complete();
    clearTimeout(this.timeInterval);
    if (this.scanUpdateSubscription) {
      this.scanUpdateSubscription.unsubscribe();
    }
    if (this.uploadProgressSubscription) {
      this.uploadProgressSubscription.unsubscribe();
    }
  }

  private init(): void {
    const type: string | undefined = this.assetPreviewDirective.contentType || this.assetPreviewDirective.type;
    this.clearPreviousState();

    if (!type) {
      this.handleNoTypeState();
      return;
    }

    if (this.assetPreviewDirective.isUploading) {
      this.handleUploadingState();
      return;
    }

    if (this.assetPreviewDirective?.malwareScanningStatus !== MalwareScanningStatus.Clean) {
      this.handleMalwareScanningState();
      return;
    }

    this.handleFilePreview(type);
  }

  private handleNoTypeState(): void {
    if (this.assetPreviewDirective.status === 'uploading') {
      const infoEl: HTMLDivElement = this.showUploadingState();
      this.elementRef.nativeElement.appendChild(infoEl);
    }
  }

  private handleUploadingState(): void {
    if (this.assetPreviewId) {
      this.setupUploadProgressSubscription();
      const initialProgress: number = this.transferService.uploadRecord[this.assetPreviewId]?.progress || 0;
      const infoEl: HTMLDivElement = this.showUploadProgress(initialProgress);
      this.elementRef.nativeElement.appendChild(infoEl);
    } else {
      const infoEl: HTMLDivElement = this.showUploadingState();
      this.elementRef.nativeElement.appendChild(infoEl);
    }
  }

  private setupUploadProgressSubscription(): void {
    if (!this.uploadProgressSubscription) {
      this.uploadProgressSubscription = listenerUploadProgressChanged
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe((file: MTMCustomFile) => {
          if (file?.dbFileObject?.id === this.assetPreviewId) {
            this.updateUploadProgress(file.progress);
          }
        });
    }
  }

  private handleMalwareScanningState(): void {
    if (
      this.assetPreviewDirective.malwareScanningStatus === MalwareScanningStatus.Unscanned &&
      this.assetPreviewDirective.projectId
    ) {
      this.setupMalwareScanMonitoring();
    }

    const infoEl: HTMLDivElement = this.showScanInfo();
    this.elementRef.nativeElement.appendChild(infoEl);
  }

  private setupMalwareScanMonitoring(): void {
    if (this.scanUpdateSubscription) {
      this.scanUpdateSubscription.unsubscribe();
    }
    const update$: Observable<FileInfo | null> = this.scanMonitorService.monitorFile({
      fileId: this.assetPreviewDirective.id,
      projectId: this.assetPreviewDirective.projectId,
    }) as Observable<FileInfo | null>;
    this.scanUpdateSubscription = update$.subscribe((data: FileInfo | null) => {
      if (!data) {
        return;
      }

      this.fileUtilsService.copyFilePropsAfterScanning(data, this.assetPreviewDirective);
      this.assetPreviewDirective.malwareScanningStatus = data.malwareScanningStatus;
      if (data.malwareScanningStatus !== MalwareScanningStatus.Unscanned) {
        if (this.scanUpdateSubscription) {
          this.scanUpdateSubscription.unsubscribe();
        }
        this.init();
      }
    });
  }

  private handleFilePreview(type: string): void {
    const [itemType]: string[] = type.split('/');

    if (
      itemType === AssetPreviewMediaType.IMAGE &&
      !this.fileTypeService.isConvertibleImageFormat(this.assetPreviewDirective)
    ) {
      this.handleImagePreview();
    } else if (itemType === AssetPreviewMediaType.VIDEO) {
      this.handleVideoPreview();
    } else if (itemType === AssetPreviewMediaType.AUDIO) {
      this.handleAudioPreview();
    } else if (this.isDocumentType(type)) {
      this.handleDocumentPreview(type);
    } else if (this.fileTypeService.isWordMimeType(type)) {
      this.previewImage(FILE_ICONS.doc);
    } else if (this.fileTypeService.isExcelMimeType(type)) {
      this.previewImage(FILE_ICONS.xls);
    } else if (this.fileTypeService.isConvertibleImageFormat(this.assetPreviewDirective)) {
      this.handleConvertibleImageFormat(type);
    } else if (this.fileTypeService.isOfficeMimeType(type)) {
      this.previewImage(FILE_ICONS.default);
    } else {
      this.previewImage(FILE_ICONS.default);
    }
  }

  private handleImagePreview(): void {
    const thumbnailExtension: string = this.getThumbnailExtension();
    const baseUrl: string | undefined = this.assetPreviewDirective.signedURL || this.assetPreviewDirective.previewImage;
    const thumbnailUrl: string = `${baseUrl}${thumbnailExtension}`;
    const img: HTMLImageElement = new Image();
    img.onload = (): void => {
      this.elementRef.nativeElement.style.backgroundImage = `url(${thumbnailUrl})`;
    };
    img.onerror = (): void => {
      this.elementRef.nativeElement.style.backgroundImage = `url(${baseUrl})`;
    };
    img.src = thumbnailUrl;
  }

  private handleVideoPreview(): void {
    const thumbnailExtension: string = this.getThumbnailExtension();
    const baseUrl: string | undefined = this.assetPreviewDirective.signedURL || this.assetPreviewDirective.previewImage;
    const thumbnailUrl: string = `${baseUrl}${thumbnailExtension}`;
    this.showImage(thumbnailUrl, this.elementRef);
  }

  private handleAudioPreview(): void {
    const previewImage: string = FILE_ICONS.audio;
    const imageEl: HTMLImageElement = document.createElement('img');
    this.elementRef.nativeElement.classList.add('audio-image');
    imageEl.src = previewImage;
    imageEl.setAttribute('width', '50%');
    imageEl.classList.add('image-file', 'icon-file');
    this.elementRef.nativeElement.appendChild(imageEl);
    this.childEl = imageEl;
  }

  private isDocumentType(type: string): boolean {
    return (
      type === AssetPreviewMediaType.APPLICATION_PDF ||
      this.fileTypeService.isPowerPointFile(this.assetPreviewDirective) ||
      this.fileTypeService.isKeynoteFile(this.assetPreviewDirective)
    );
  }

  private handleDocumentPreview(type: string): void {
    let imageUrl: string = this.getDocumentThumbnailUrl();
    if (!imageUrl) {
      imageUrl = FILE_ICONS.pdf;
    }
    const fallbackUrl: string = this.getDocumentFallbackUrl(type);
    this.previewImage(imageUrl, fallbackUrl);
  }

  private getDocumentThumbnailUrl(): string {
    if (this.assetPreviewDirective.transcodeMetadata) {
      const pngThumbnail: FileTranscodeInfo | undefined = this.assetPreviewDirective.transcodeMetadata.find(
        (item: FileTranscodeInfo) => item.codec === FileExtension.PNG
      );
      if (pngThumbnail && this.assetPreviewDirective.signedURL) {
        return `${this.assetPreviewDirective.signedURL}?thumbnail=${pngThumbnail.quality}`;
      }
    }
    if (this.assetPreviewDirective.isEditSection && this.assetPreviewDirective.signedURL) {
      return `${this.assetPreviewDirective.signedURL}?thumbnail=_TINY`;
    }
    return '';
  }

  private getDocumentFallbackUrl(type: string): string {
    if (type === AssetPreviewMediaType.APPLICATION_PDF) {
      return FILE_ICONS.pdf;
    }
    const extension: string = this.fileUtilsService.getFileExtension(this.assetPreviewDirective?.name || '');
    switch (extension) {
      case FileExtension.PPT:
      case FileExtension.PPTX:
        return FILE_ICONS.ppt;
      case FileExtension.DOC:
      case FileExtension.DOCX:
        return FILE_ICONS.doc;
      case FileExtension.KEYNOTE:
        return FILE_ICONS.slider;
      default:
        return FILE_ICONS.default;
    }
  }

  private handleConvertibleImageFormat(type: string): void {
    let imageUrl: string = FILE_ICONS.img;
    // if (type === AssetPreviewMediaType.IMAGE_TIFF) {
    //   imageUrl = AssetIconPaths.TIFF;
    // } else if (type === AssetPreviewMediaType.IMAGE_PHOTOSHOP || type === AssetPreviewMediaType.IMAGE_X_PHOTOSHOP) {
    //   imageUrl = AssetIconPaths.PSD;
    // } else if (type === AssetPreviewMediaType.APPLICATION_3GPP_PIC) {
    //   imageUrl = AssetIconPaths.PSB;
    // }
    this.previewImage(imageUrl);
  }

  private getThumbnailExtension(): string {
    if (this.assetPreviewThumbnailPosition === 'vertical') {
      return '?thumbnail=_SMALL_209_241';
    }
    if (this.assetPreviewThumbnailPosition === 'horizontal') {
      return '?thumbnail=_SMALL_211_121';
    }
    return '?thumbnail=_SMALL';
  }

  private clearPreviousState(): void {
    if (this.childEl) {
      this.childEl.remove();
    }
    const classesToRemove: string[] = ['audio-image', 'file-image'];
    classesToRemove.forEach((className: string) => {
      this.elementRef.nativeElement.classList.remove(className);
    });
    this.elementRef.nativeElement.style.backgroundImage = '';
    this.childEl = null;
    this.elementRef.nativeElement.innerHTML = '';
    if (this.scanUpdateSubscription) {
      this.scanUpdateSubscription.unsubscribe();
    }
  }

  private previewImage(imageUrl: string, fallbackUrl: string = ''): void {
    const imageEl: HTMLImageElement = document.createElement('img');
    this.elementRef.nativeElement.classList.add('file-image');
    imageEl.src = imageUrl;
    const { index } = this.assetPreviewDirective;
    const { thumbLength } = this.assetPreviewDirective;
    let imgWidth: string;
    if (!this.assetPreviewDirective.isEditSection) {
      imgWidth = '50%';
    } else if (index == 2 || index == 3) {
      imgWidth = '30%';
    } else if (index == 1 && thumbLength > 1) {
      imgWidth = '30%';
    } else if (thumbLength == 1) {
      imgWidth = '40%';
    } else {
      imgWidth = '50%';
    }
    imageEl.setAttribute('width', imgWidth);
    imageEl.classList.add('image-file');
    if (/\/file-ext\/[^.]+\.svg$/.test(imageUrl)) {
      imageEl.classList.add('icon-file');
    }

    if (this.assetPreviewDirective.displayName) {
      const titleAttr: string = this.fileUtilsService.getFileDisplayName(this.assetPreviewDirective.displayName);
      imageEl.setAttribute('title', titleAttr);
    }
    if (fallbackUrl) {
      imageEl.onerror = (): void => {
        imageEl.src = fallbackUrl;
        imageEl.classList.add('icon-file');
      };
    }
    this.elementRef.nativeElement.appendChild(imageEl);
    this.childEl = imageEl;
  }

  private showImage(thumbnailUrl: string, elementRef: ElementRef, retry: boolean = true): void {
    const img: HTMLImageElement = new Image();
    img.onload = (): void => {
      const iconElement: Element | null = elementRef.nativeElement.querySelector('i');
      if (iconElement) {
        iconElement.remove();
      }
      elementRef.nativeElement.style.backgroundImage = `url(${thumbnailUrl})`;
      elementRef.nativeElement.style.backgroundSize = 'cover';
    };
    img.onerror = (): void => {
      elementRef.nativeElement.style.backgroundImage = "url('/assets/img/video-file.png')";
      elementRef.nativeElement.style.backgroundSize = 'contain';
      if (retry && document.body.contains(elementRef.nativeElement)) {
        this.timeInterval = setTimeout((): void => {
          this.showImage(thumbnailUrl, elementRef);
        }, this.retryTime);
        if (this.retryTime < 40000) this.retryTime *= 2;
      }
    };
    img.src = thumbnailUrl;
  }

  private showUploadingState(): HTMLDivElement {
    const loader: HTMLDivElement = document.createElement('div');
    loader.classList.add('uploading-loader');
    const textDiv: HTMLDivElement = document.createElement('div');
    textDiv.classList.add('uploading-text');
    textDiv.innerText = this.translatePipe.transform('uploading');
    const divEl: HTMLDivElement = document.createElement('div');
    divEl.classList.add(
      'd-flex',
      'position-relative',
      'align-items-center',
      'justify-content-center',
      'asset-preview-uploading',
      'h-100'
    );
    divEl.appendChild(loader);
    divEl.appendChild(textDiv);
    return divEl;
  }

  private showScanInfo(): HTMLDivElement {
    const status: MalwareScanningStatus | undefined = this.assetPreviewDirective.malwareScanningStatus;
    if (status === MalwareScanningStatus.Quarantined) {
      return this.showQuarantinedStatus();
    }
    return this.showProcessingFileStatus();
  }

  private showProcessingFileStatus(text: string = 'asset_processingFile'): HTMLDivElement {
    const divEl: HTMLDivElement = document.createElement('div');
    divEl.classList.add('asset-preview-processing', 'd-flex', 'align-items-center', 'justify-content-center', 'h-100');
    divEl.innerText = this.translatePipe.transform(text);
    return divEl;
  }

  private showQuarantinedStatus(): HTMLDivElement {
    const iEl: HTMLElement = document.createElement('i');
    iEl.classList.add('fa');
    iEl.style.fontSize = '1.2rem';
    iEl.classList.add('fa-times-circle', 'text-danger');
    const divEl: HTMLDivElement = document.createElement('div');
    divEl.classList.add(
      'd-flex',
      'bg-light',
      'align-items-center',
      'justify-content-center',
      'flex-column',
      'scan-info',
      'h-100'
    );
    divEl.appendChild(iEl);
    return divEl;
  }

  private showUploadProgress(percentage: number): HTMLDivElement {
    const radius: number = 45;
    const strokeWidth: number = 9;
    const svgNS: string = 'http://www.w3.org/2000/svg';
    const svg: SVGSVGElement = document.createElementNS(svgNS, 'svg') as SVGSVGElement;
    svg.setAttribute('viewBox', '0 0 100 100');
    svg.classList.add('circular-progress-bar');
    svg.style.maxHeight = '100%';

    const group: SVGGElement = document.createElementNS(svgNS, 'g') as SVGGElement;
    group.setAttribute('transform', 'rotate(-90 50 50)');

    const circleBackground: SVGCircleElement = document.createElementNS(svgNS, 'circle') as SVGCircleElement;
    circleBackground.classList.add('bg-bar');
    circleBackground.setAttribute('cx', '50');
    circleBackground.setAttribute('cy', '50');
    circleBackground.setAttribute('r', `${radius}`);
    circleBackground.setAttribute('stroke', 'white');
    circleBackground.setAttribute('stroke-width', strokeWidth.toString());
    circleBackground.setAttribute('fill', 'none');
    group.appendChild(circleBackground);

    const circumference: number = 2 * Math.PI * radius;
    const offset: number = circumference - (percentage / 100) * circumference;
    const strokeDasharray: string = `${circumference} ${circumference}`;
    const strokeDashoffset: number = offset;

    const circleProgress: SVGCircleElement = document.createElementNS(svgNS, 'circle') as SVGCircleElement;
    circleProgress.classList.add('progress-bar');
    circleProgress.setAttribute('cx', '50');
    circleProgress.setAttribute('cy', '50');
    circleProgress.setAttribute('r', '45');
    circleProgress.setAttribute('stroke', '#43CB9A');
    circleProgress.setAttribute('stroke-width', strokeWidth.toString());
    circleProgress.setAttribute('fill', 'none');
    circleProgress.setAttribute('stroke-dasharray', strokeDasharray);
    circleProgress.setAttribute('stroke-dashoffset', `${strokeDashoffset}`);
    group.appendChild(circleProgress);
    svg.appendChild(group);
    const outerEl: HTMLDivElement = document.createElement('div');
    outerEl.classList.add(
      'asset-preview-upload-progress',
      'position-relative',
      'd-flex',
      'align-items-center',
      'justify-content-center',
      'h-100'
    );
    const innerEl: HTMLDivElement = document.createElement('div');
    innerEl.classList.add(
      'position-absolute',
      'w-100',
      'h-100',
      'd-flex',
      'align-items-center',
      'justify-content-center'
    );
    innerEl.style.left = '0';
    innerEl.style.top = '0';
    innerEl.appendChild(svg);
    outerEl.appendChild(innerEl);

    const span: HTMLSpanElement = document.createElement('span');
    span.classList.add('progress-text');
    span.innerText = this.translatePipe.transform('uploading');
    outerEl.appendChild(span);
    return outerEl;
  }

  private updateUploadProgress(percentage: number): void {
    const radius: number = 45;
    const circleProgress: Element | null = this.elementRef.nativeElement.querySelector(
      '.asset-preview-upload-progress circle.progress-bar'
    );
    if (!circleProgress) {
      return;
    }
    const circumference: number = 2 * Math.PI * radius;
    const offset: number = circumference - (percentage / 100) * circumference;
    circleProgress.setAttribute('stroke-dashoffset', `${offset}`);
  }
}
